import { styled } from '@linaria/react';

import { bodyFont, colors, fonts, layout, primitives, semantic, space } from '../../theme/tokens';

export const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

export const GalleryFrame = styled.div`
  --image-frame-mobile-height: ${layout.heroImageMobileHeight};
  --image-frame-desktop-height: 310px;
  --image-frame-desktop-padding: 0px;
  --image-frame-desktop-radius: ${layout.borderRadiusLg};
  position: relative;
  margin: 0 calc(-1 * ${space[5]});
  overflow: hidden;
  border-radius: ${layout.borderRadiusXl} ${layout.borderRadiusXl} 0 0;

  @media (min-width: ${layout.desktopBreakpoint}) {
    margin: ${space[5]} 0;
    border-radius: ${layout.borderRadiusLg};
  }
`;

export const CloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${space[3]};
  margin: 0 calc(-1 * ${space[5]});
  border-bottom: 1px solid ${semantic.border.default};

  @media (min-width: ${layout.desktopBreakpoint}) {
    border-bottom: none;
  }
`;

export const CloseControl = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 ${space[4]};
  border: none;
  border-radius: ${layout.borderRadiusFull};
  background: ${colors.white};
  color: ${semantic.icon.primary};
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${semantic.bg.surfaceSubtle};
  }

  &[data-placement='floating'] {
    position: absolute;
    top: ${space[5]};
    right: ${space[5]};
    z-index: 2;
  }

  @media (min-width: ${layout.desktopBreakpoint}) {
    height: 48px;
    padding: 0 ${space['20px']};
  }
`;

export const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[7]};
  padding: ${space[7]} 0;

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space[4]} ${space[5]};

    &[data-has-footer='false'] {
      padding-bottom: ${space[8]};
    }
  }
`;

export const MainBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[5]};
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space[4]};
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
`;

export const ModalTitle = styled.h2`
  font-family: ${bodyFont};
  color: ${semantic.text.primary};
  line-height: ${primitives.typography.lineHeight.normal};
  margin: 0;

  &[data-title-variant='inline'] {
    font-size: ${fonts.size.base};
    font-weight: ${fonts.weight.normal};
  }

  &[data-title-variant='prominent'] {
    font-size: ${fonts.size.base};
    font-weight: ${fonts.weight.medium};
  }

  @media (min-width: ${layout.desktopBreakpoint}) {
    &[data-title-variant='prominent'] {
      font-size: ${fonts.size.lg};
    }
  }
`;

export const MetaText = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  color: ${semantic.text.secondary};
`;

export const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${space[1]};
  flex-shrink: 0;
  text-align: right;
`;

export const StayPrice = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};
`;

export const NightPrice = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  color: ${semantic.text.primary};
`;

export const RichText = styled.div`
  font-family: ${bodyFont};
  line-height: ${primitives.typography.lineHeight.normal};

  &[data-variant='description'] {
    font-size: ${fonts.size.base};
    color: ${semantic.text.primary};
  }

  &[data-variant='terms'] {
    font-size: ${fonts.size.xs};
    color: ${semantic.text.secondary};
  }

  & p {
    margin: 0;
  }

  & p + p {
    margin-top: ${space[3]};
  }
`;

export const AmenityList = styled.ul`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  color: ${semantic.text.primary};
  margin: 0;
  display: flex;
  flex-direction: column;

  &[data-variant='bulleted'] {
    list-style: disc;
    padding-left: ${space[5]};
    gap: ${space[4]};
  }

  &[data-variant='detailed'] {
    list-style: none;
    padding: 0;
  }

  &[data-variant='detailed'] li {
    display: flex;
    align-items: center;
    gap: ${space[3]};
    padding-block: ${space[5]};
    border-bottom: 1px solid ${semantic.border.default};
  }

  &[data-variant='detailed'] li:last-child {
    border-bottom: none;
  }
`;

export const AmenityIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${semantic.icon.primary};
`;

export const TotalDueRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space[4]};
  border-top: 1px solid ${semantic.border.default};
  padding-top: ${space[5]};
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  color: ${semantic.text.primary};
`;

export const TotalDueAmount = styled.span`
  font-weight: ${fonts.weight.medium};
`;

export const TermsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
`;

export const TermsHeading = styled.h3`
  font-family: ${bodyFont};
  font-size: ${fonts.size.xs};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};
  margin: 0;
`;

export const FooterContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  padding: ${space[3]} ${space['20px']};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space[5]} ${space[4]};
  }
`;
